import { CURRENT_CLIENT } from '@/constants/index'

export type SignupRoleConfig = {
  id: string
  label: string
  description?: string
  icon?: string
  group: string
  enabled?: boolean
}

export type SignupLegalConfig = {
  termsUrl: string
  privacyUrl: string
  termsLabel?: string
  privacyLabel?: string
}

export type SignupMode = 'open' | 'waitlist' | 'approval' | 'invite_only'

export type AccessRequestFieldOption = {
  value: string
  label: string
}

export type AccessRequestFieldConfig = {
  id: string
  type: 'text' | 'select' | 'textarea'
  label?: string
  placeholder?: string
  hint?: string
  required?: boolean
  options?: AccessRequestFieldOption[]
}

export type AccessRequestConfig = {
  heading?: string
  submitLabel?: string
  successTitle?: string
  successMessage?: string
  submitMessage?: string
  sendAckEmail?: boolean
  requestType?: string
  adminLabel?: string
  adminDescription?: string
  emailHint?: string
  emailLabel?: string
  emailPlaceholder?: string
  hideRegisterPage?: boolean
  fields?: AccessRequestFieldConfig[]
  /** Pack-owned keys (read by the pack's `accessRequests.admin` plugin). */
  [key: string]: unknown
}

export type SignupConfig = {
  mode?: SignupMode
  heading?: string
  lockRoles?: boolean
  roles?: SignupRoleConfig[]
  legal?: Partial<SignupLegalConfig>
  accessRequest?: AccessRequestConfig
}

export function getSignupConfig(): SignupConfig {
  return ((CURRENT_CLIENT as { signup?: SignupConfig }).signup ?? {}) as SignupConfig
}

export function getSignupMode(): SignupMode {
  const mode = getSignupConfig().mode
  if (mode === 'waitlist' || mode === 'approval' || mode === 'invite_only') return mode
  return 'open'
}

/** True when direct self-service registration is closed for this tenant. */
export function registrationClosed(): boolean {
  const mode = getSignupMode()
  return mode === 'waitlist' || mode === 'invite_only'
}

export function waitlistEnabled(): boolean {
  return getSignupMode() === 'waitlist'
}

export function accessRequestEnabled(): boolean {
  const mode = getSignupMode()
  return mode === 'waitlist' || mode === 'invite_only'
}

const DEFAULT_ACCESS_REQUEST: Record<SignupMode, AccessRequestConfig> = {
  open: {},
  waitlist: {
    heading: 'Join the waitlist',
    submitLabel: 'Join the waitlist',
    successTitle: 'Thank you',
    successMessage: "We've received your details and will be in touch when early access opens.",
  },
  approval: {
    heading: 'Create an account',
    submitLabel: 'Sign up',
    successTitle: 'Thanks — you are on the list',
    successMessage: 'We will review your account and email you when you can sign in.',
    adminLabel: 'Signups',
    adminDescription: 'Review new accounts and approve people who should have access.',
  },
  invite_only: {
    heading: 'Request access',
    submitLabel: 'Request access',
    successTitle: 'Thank you',
    successMessage: "We'll review your request and email you if access is approved.",
  },
}

export function getAccessRequestConfig(): AccessRequestConfig {
  const mode = getSignupMode()
  const configured = getSignupConfig().accessRequest ?? {}
  return { ...DEFAULT_ACCESS_REQUEST[mode], ...configured }
}

export function getSignupLegalConfig(): SignupLegalConfig | null {
  const legal = getSignupConfig().legal
  if (!legal?.termsUrl?.trim() || !legal?.privacyUrl?.trim()) return null
  return {
    termsUrl: legal.termsUrl.trim(),
    privacyUrl: legal.privacyUrl.trim(),
    termsLabel: legal.termsLabel,
    privacyLabel: legal.privacyLabel,
  }
}

export function getSignupRoles(): SignupRoleConfig[] {
  const roles = getSignupConfig().roles ?? []
  return roles.filter((role) => role.enabled !== false)
}

export function signupRolesRequired(): boolean {
  return getSignupRoles().length > 0
}
